// Household Income — one ledger with member-owned editors. The household-wide
// Income Type action uses one aggregate-backed `PUT /household-income` operation.
//
// The replace is deliberate on the server side: income is verified as a set, and "no
// reported income" has to be expressible. So the editor works on a member's whole
// collection and echoes each line's id back, which is how the server knows which line a row
// means; a line without an id is a new one.
//
// Every figure shown here is computed locally only to show staff the effect of what they are
// typing. The stored figure is whatever the next read returns.
import { useEffect, useId, useRef, useState } from "react"
import { useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { useBenefitIncomeDraft, useIncomeHistoryPreference } from "@/features/benefit-workspace/useBenefitIncomeDraft"
import { captureIncomeAnchor, incomeMemberBaseline, type IncomeLineDraft, type IncomeMemberBaseline } from "@/features/benefit-workspace/benefitIncomeDrafts"
import { IconChevronDown, IconChevronRight, IconHistory, IconCurrencyDollar, IconPlus, IconTrash } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { ButtonGroup } from "@/components/ui/button-group"
import { RecordAction } from "@/components/benefit/record-action"
import { TooltipProvider } from "@/components/ui/tooltip"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { toast } from "sonner"
import { IncomeAssessmentBanner, useIncomeAssessment } from "@/components/benefit/income-assessment-banner"
import { api } from "@/lib/api"
import { incomeSourceLabels, relationshipLabels } from "@/lib/benefit-enums"
import { enumLabel, fmtMoney } from "@/lib/benefit-format"
import { formatPersonName } from "@/lib/person-identity"
import { prependIncomeSource } from "@/lib/income-editor"
import { saveBenefitChange } from "@/lib/benefit-save"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { hasMixedIncomeAdjustments, householdIncomeTypeLabel, incomeMembersByDob } from "@/lib/income-summary"
import { householdIncomePeriods, incomeEditBlocker, incomeEvidenceLabel, incomePeriodLabel, incomeRecordedDate, memberIncomeEvidence, savedAdjustmentLabel, savedIncomeFigures, type IncomeAssessment } from "@/lib/income-ledger"
import "./household-income-section.css"
import type { BenefitDetailDto, IncomeDto, PersonDto } from "@/lib/benefit-types"
import {
  ADJUSTMENT_FIFTEEN_PERCENT,
  ADJUSTMENT_NONE,
  adjustmentFromFlags,
  adjustmentOptions,
  applicationIncomeAnchor,
  incomeHistoryAnchor,
  incomeMonthAnchor,
  householdMonthlyAdjustedIncome,
  incomeHistoryLabels,
  incomeLineFigures,
  monthsFor,
  regenerateAmounts,
  ONE_MONTH,
  THREE_MONTH,
  validateIncomeLine,
} from "@/lib/income"

const incomeSourceOptions = Object.entries(incomeSourceLabels)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => (a.label < b.label ? -1 : 1))

const historyTypeOptions = Object.entries(incomeHistoryLabels).map(([value, label]) => ({
  value,
  label,
}))

let nextLineKey = 0

function incomeLineDraft(income: IncomeDto, index = 0): IncomeLineDraft {
  return {
    key: `existing-${income.id}-${index}`,
    id: income.id,
    sourceOfIncome: income.sourceOfIncome === null ? "" : String(income.sourceOfIncome),
    incomeType: String(income.incomeType),
    adjustment: adjustmentFromFlags(income),
    description: income.description ?? "",
    amounts: [...income.incomeAmountCollection]
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .map((amount) => ({
        id: amount.id,
        date: amount.date.slice(0, 10),
        amount: String(amount.amount),
      })),
  }
}

function newIncomeLine(asOf: Date, incomeType: number): IncomeLineDraft {
  nextLineKey += 1
  return {
    key: `new-${nextLineKey}`,
    sourceOfIncome: "",
    incomeType: String(incomeType),
    adjustment: ADJUSTMENT_NONE,
    description: "",
    amounts: monthsFor(incomeType, asOf).map((date) => ({ date, amount: "" })),
  }
}

function monthLabel(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  })
}

function memberName(person: Pick<PersonDto, "firstName" | "lastName" | "middleName">): string {
  const name = formatPersonName(person)
  return name === "—" ? "Household member" : name
}

function incomeRequestLine(line: IncomeLineDraft) {
  return {
    ...(line.id ? { id: line.id } : {}),
    sourceOfIncome: Number(line.sourceOfIncome),
    incomeType: Number(line.incomeType),
    adjustment: line.adjustment,
    description: line.description.trim() || null,
    amounts: line.amounts.map((amount) => ({
      ...(amount.id ? { id: amount.id } : {}),
      date: amount.date,
      amount: Number(amount.amount.trim().replace(/[$,]/g, "") || "0"),
    })),
  }
}

function bulkIncomeLines(person: IncomeMemberBaseline, incomeType: number, applicationDate: string): { lines: ReturnType<typeof incomeRequestLine>[]; error: string | null } {
  if (person.incomeCollection.some(hasMixedIncomeAdjustments)) {
    return { lines: [], error: `${memberName(person)} has legacy mixed income adjustments. Review that member individually before changing the household Income Type.` }
  }
  const lines: IncomeLineDraft[] = person.incomeCollection.map(incomeLineDraft)
  for (const line of lines) {
    if (!line.sourceOfIncome) return { lines: [], error: `Income source is missing for ${memberName(person)}.` }
    const nextAmounts = regenerateAmounts(line.amounts, incomeType, incomeHistoryAnchor(line.amounts, applicationIncomeAnchor(applicationDate)))
    const nextDates = new Set(nextAmounts.map((amount) => amount.date))
    const dropped = line.amounts.some((amount) =>
      !nextDates.has(amount.date) && Number(amount.amount.replace(/[$,]/g, "") || "0") !== 0,
    )
    if (dropped) {
      return {
        lines: [],
        error: `Changing ${memberName(person)} to ${incomeHistoryLabels[incomeType] ?? "the selected type"} would remove a reported month. Update that member's history individually first.`,
      }
    }
    const invalid = validateIncomeLine(nextAmounts, incomeType, Number(line.sourceOfIncome))
    if (invalid) return { lines: [], error: `${memberName(person)}: ${invalid}` }
    line.incomeType = String(incomeType)
    line.amounts = nextAmounts
  }
  return { lines: lines.map(incomeRequestLine), error: null }
}

function IncomeLineEditor({
  line,
  asOf,
  disabled,
  errors,
  onChange,
  onRemove,
}: {
  line: IncomeLineDraft
  asOf: Date
  disabled: boolean
  errors: string[]
  onChange: (next: IncomeLineDraft) => void
  onRemove: () => void
}) {
  const set = (patch: Partial<IncomeLineDraft>) => onChange({ ...line, ...patch })
  const figures = incomeLineFigures(line.amounts, Number(line.incomeType), line.adjustment)
  const isLegacyAdjustment = line.adjustment === ADJUSTMENT_FIFTEEN_PERCENT

  return (
    <div className="income-source-editor" data-income-editor-source>
      <div className="income-edit-fields">
        <div className="space-y-1.5">
          <Label htmlFor={`${line.key}-source`}>Source</Label>
          <Select
            value={line.sourceOfIncome}
            disabled={disabled}
            onValueChange={(sourceOfIncome) => set({ sourceOfIncome })}
          >
            <SelectTrigger id={`${line.key}-source`} className="w-full">
              <SelectValue placeholder="Select source" />
            </SelectTrigger>
            <SelectContent>
              {incomeSourceOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${line.key}-income-type`}>Income Type</Label>
          <Select
            value={line.incomeType}
            disabled={disabled}
            onValueChange={(incomeType) =>
              set({
                incomeType,
                amounts: regenerateAmounts(line.amounts, Number(incomeType), incomeHistoryAnchor(line.amounts, asOf)),
              })
            }
          >
            <SelectTrigger id={`${line.key}-income-type`} className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {historyTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${line.key}-ending-month`}>Last Reported Month</Label>
          <Input id={`${line.key}-ending-month`} type="month" disabled={disabled}
            value={line.amounts.map(a => a.date.slice(0, 7)).sort().at(-1) ?? ""}
            onChange={event => {
              const anchor = incomeMonthAnchor(event.target.value)
              if (anchor) set({ amounts: regenerateAmounts(line.amounts, Number(line.incomeType), anchor) })
            }} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${line.key}-adjustment`}>Adjustment</Label>
          {isLegacyAdjustment ? (
            // 15 % is not offered for new selections — the arithmetic ignores it — but an
            // existing row keeps it, unchanged, rather than being silently re-answered.
            <output className="block pt-2 text-sm text-muted-foreground" id={`${line.key}-adjustment`}>
              15% (legacy) — kept as entered
            </output>
          ) : (
            <Select
              value={String(line.adjustment)}
              disabled={disabled}
              onValueChange={(adjustment) => set({ adjustment: Number(adjustment) })}
            >
              <SelectTrigger id={`${line.key}-adjustment`} className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {adjustmentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${line.key}-description`}>Description (optional)</Label>
          <Input
            id={`${line.key}-description`}
            maxLength={255}
            placeholder="Employer or detail"
            value={line.description}
            disabled={disabled}
            onChange={(event) => set({ description: event.target.value })}
          />
        </div>
      </div>

      <div className="income-month-fields">
        {line.amounts.map((entry, index) => (
          <div key={entry.date} className="space-y-1">
            <Label htmlFor={`${line.key}-amount-${index}`} className="text-xs text-muted-foreground">
              {monthLabel(entry.date)}
            </Label>
            <Input
              id={`${line.key}-amount-${index}`}
              inputMode={line.sourceOfIncome === "1" ? "text" : "decimal"}
              value={entry.amount}
              disabled={disabled}
              onChange={(event) =>
                set({
                  amounts: line.amounts.map((amount, position) =>
                    position === index ? { ...amount, amount: event.target.value } : amount,
                  ),
                })
              }
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex flex-wrap gap-4 text-muted-foreground">
          <span>Gross {fmtMoney(figures.gross)}</span>
          <span>Monthly {fmtMoney(figures.monthly)}</span>
          <span className="font-medium text-foreground">
            Preview monthly adjusted {fmtMoney(figures.monthlyAdjusted)}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onRemove} disabled={disabled}>
          <IconTrash className="size-4" />
          Remove source
        </Button>
      </div>

      {errors.length > 0 && (
        <p className="text-sm text-destructive">{errors.join(" ")}</p>
      )}
    </div>
  )
}

function MemberIncomeRecord({
  benefit,
  person,
  canEdit,
  defaultIncomeType,
  onUpdated,
  assessment,
}: {
  benefit: BenefitDetailDto
  person: PersonDto
  canEdit: boolean
  defaultIncomeType: number
  onUpdated: (updated: BenefitDetailDto) => void
  assessment: IncomeAssessment | null
}) {
  const [isEditing, setIsEditing, editToken] = useBenefitEdit(benefit.concurrencyToken, `member-income:${person.id}`)
  const [isSaving, setIsSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const busy = useRef(false)
  const origin = useRef<HTMLButtonElement | null>(null)
  const heading = useRef<HTMLHeadingElement | null>(null)
  const titleId = useId()
  const panel = useRef<HTMLDivElement | null>(null)
  const blocked = incomeEditBlocker(person)
  const [lines, setLines] = useBenefitIncomeDraft(`member-income:${person.id}`, "lines", () => person.incomeCollection.map(incomeLineDraft))
  const [fieldErrors, setFieldErrors] = useBenefitIncomeDraft(`member-income:${person.id}`, "fieldErrors", {})
  const [anchorDate, setAnchorDate] = useBenefitIncomeDraft(`member-income:${person.id}`, "anchorDate", () => captureIncomeAnchor(benefit.applicationDate))
  const [entryIncomeType, setEntryIncomeType] = useBenefitIncomeDraft(`member-income:${person.id}`, "defaultIncomeType", defaultIncomeType)
  const firstKey = lines[0]?.key
  useEffect(() => {
    if (isEditing && firstKey?.startsWith("new-")) {
      const source = panel.current?.querySelector<HTMLButtonElement>('button[id$="-source"]')
      source?.focus(); source?.scrollIntoView({ block: "nearest" })
    }
  }, [isEditing, firstKey])

  useEffect(() => {
    if (!isEditing) {
      setLines(person.incomeCollection.map(incomeLineDraft))
      setFieldErrors({})
      setAnchorDate(captureIncomeAnchor(benefit.applicationDate))
      setEntryIncomeType(defaultIncomeType)
    }
  }, [person, isEditing, benefit.applicationDate, defaultIncomeType])

  const save = async () => {
    if (busy.current || isSaving || reviewing || !canEdit || blocked) return
    for (const line of lines) {
      if (!line.sourceOfIncome) {
        toast.error("Choose an income source for every reported line.")
        return
      }
      const invalid = validateIncomeLine(line.amounts, Number(line.incomeType), Number(line.sourceOfIncome))
      if (invalid) {
        toast.error(invalid)
        return
      }
    }

    const body = {
      concurrencyToken: editToken,
      income: lines.map((line) => ({
        // Echoing the id is how the server knows which line this is; omitting it means new.
        ...(line.id ? { id: line.id } : {}),
        sourceOfIncome: Number(line.sourceOfIncome),
        incomeType: Number(line.incomeType),
        adjustment: line.adjustment,
        description: line.description.trim() || null,
        amounts: line.amounts.map((amount) => ({
          date: amount.date,
          amount: Number(amount.amount.trim().replace(/[$,]/g, "") || "0"),
        })),
      })),
    }

    busy.current = true
    setIsSaving(true)
    const { saved, reloaded, fieldErrors: errors } = await saveBenefitChange(
      benefit.id,
      onUpdated,
      () =>
        api.put<BenefitDetailDto>(
          `/api/benefitapplications/${benefit.id}/household-members/${person.id}/income`,
          body,
        ),
      `Income updated for ${memberName(person)}.`,
    )
    busy.current = false
    setIsSaving(false)
    setFieldErrors(errors)
    if (saved || reloaded) setIsEditing(false)
  }

  const errorsForLine = (index: number): string[] =>
    Object.entries(fieldErrors)
      .filter(([field]) => field.startsWith(`income[${index}]`))
      .flatMap(([, messages]) => messages)

  const draftTotal = householdMonthlyAdjustedIncome(
    lines.map((line) => ({
      amounts: line.amounts,
      incomeType: Number(line.incomeType),
      adjustment: line.adjustment,
    })),
  )

  const startEdit = (button: HTMLButtonElement, add = false) => {
    if (!canEdit || blocked || !setIsEditing(true)) return
    origin.current = button
    const saved = person.incomeCollection.map(incomeLineDraft)
    setLines(add ? prependIncomeSource(saved, newIncomeLine(applicationIncomeAnchor(benefit.applicationDate), defaultIncomeType)) : saved)
    setFieldErrors({})
    setAnchorDate(captureIncomeAnchor(benefit.applicationDate))
    setEntryIncomeType(defaultIncomeType)
  }
  const cancel = () => {
    if (busy.current || isSaving || reviewing) return
    setLines(person.incomeCollection.map(incomeLineDraft)); setFieldErrors({}); setIsEditing(false)
  }
  return <article className="income-member" data-income-member aria-labelledby={titleId}>
    <header className="income-member-header">
      <div><h4 ref={heading} tabIndex={-1} id={titleId}>{memberName(person)}</h4><Badge variant="outline">{person.id === benefit.applicantId ? "Applicant (Self)" : enumLabel(relationshipLabels, person.relationship)}</Badge></div>
      {canEdit && !blocked && <div className="income-member-actions">
        <Button variant="outline" size="sm" aria-label={`Add income source for ${memberName(person)}`} onClick={event => startEdit(event.currentTarget, true)}><IconPlus className="size-4" aria-hidden="true" />Add source</Button>
        <ButtonGroup aria-label={`Income actions for ${memberName(person)}`}><RecordAction kind="edit" label={`Edit income for ${memberName(person)}`} onClick={event => startEdit(event.currentTarget)} /></ButtonGroup>
      </div>}
    </header>
    {blocked && <p className="income-note">{blocked}</p>}
    {person.incomeCollection.length === 0 ? <p className="income-empty-member">{memberIncomeEvidence(person, assessment)}</p> : <Table className="income-source-table" aria-label={`Income sources for ${memberName(person)}`}>
      <TableHeader><TableRow><TableHead>Source / detail</TableHead><TableHead>History / reported months</TableHead><TableHead>Reported total</TableHead><TableHead>Monthly before adjustment</TableHead><TableHead>Adjustment</TableHead><TableHead>Monthly adjusted</TableHead></TableRow></TableHeader>
      <TableBody>{person.incomeCollection.map((income, index) => <IncomeSourceRecord key={`${income.id}-${index}`} income={income} name={memberName(person)} />)}</TableBody>
    </Table>}
    <Sheet open={isEditing && !reviewing} onOpenChange={open => { if (!open) toast.error("Save or cancel the open editor before closing this panel.") }}>
      <SheetContent className="income-member-sheet" onCloseAutoFocus={event => { event.preventDefault(); if (!reviewing) (origin.current?.isConnected ? origin.current : heading.current ?? document.getElementById("income-heading"))?.focus() }}>
        <SheetHeader><SheetTitle>Income · {memberName(person)}</SheetTitle><SheetDescription>Changes save this member&apos;s entire income collection. Removing a source only takes effect when you save.</SheetDescription></SheetHeader>
        <div ref={panel} className="income-panel-body">
          {lines.length === 0 && <p className="income-note">No income reported for this member. Saving an empty collection removes their saved sources; it does not confirm no income.</p>}
          {lines.map((line, index) => <IncomeLineEditor key={line.key} line={line} asOf={applicationIncomeAnchor(anchorDate)} disabled={isSaving} errors={errorsForLine(index)}
            onChange={next => setLines(current => current.map(candidate => candidate.key === line.key ? next : candidate))}
            onRemove={() => setLines(current => current.filter(candidate => candidate.key !== line.key))} />)}
          <Button variant="outline" size="sm" disabled={isSaving} onClick={() => setLines(current => prependIncomeSource(current, newIncomeLine(applicationIncomeAnchor(anchorDate), entryIncomeType)))}><IconPlus className="size-4" />Add income source</Button>
          <p className="income-draft-total">Preview member monthly adjusted <strong>{fmtMoney(draftTotal)}</strong></p>
          <p className="income-note">Draft preview only. Saved household figures update from the server after Save.</p>
        </div>
        <SheetFooter><div className="income-panel-actions"><Button variant="outline" disabled={isSaving} onClick={cancel}>Cancel</Button><Button disabled={isSaving || !canEdit || !!blocked} onClick={save}>{isSaving && <IconCurrencyDollar className="size-4 animate-pulse" />}Save income</Button></div></SheetFooter>
      </SheetContent>
    </Sheet>
  </article>
}

const money = (value: number | null) => typeof value === "number" && Number.isFinite(value) ? fmtMoney(value) : "Unavailable"

function IncomeSourceRecord({ income, name }: { income: IncomeDto; name: string }) {
  const [expanded, setExpanded] = useState(false)
  const id = useId()
  const figures = savedIncomeFigures(income)
  const source = income.sourceOfIncome == null ? "Source not recorded" : enumLabel(incomeSourceLabels, income.sourceOfIncome)
  return <>
    <TableRow data-income-source>
      <TableCell data-label="Source / detail"><div className="income-source-identity"><Button className="income-disclosure" variant="ghost" size="icon" aria-label={`${expanded ? "Hide" : "Show"} reported amounts for ${name}: ${source}${income.description ? ` · ${income.description}` : ""}`} aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(value => !value)}>{expanded ? <IconChevronDown className="size-4" /> : <IconChevronRight className="size-4" />}</Button><div><strong>{source}</strong>{income.description && <span className="income-subtext">{income.description}</span>}</div></div></TableCell>
      <TableCell data-label="History / reported months">{incomeHistoryLabels[income.incomeType] ?? `Unrecognized (${income.incomeType})`}<span className="income-subtext">{incomePeriodLabel(income.incomeAmountCollection)}</span></TableCell>
      <TableCell data-label="Reported total" className="income-money">{money(figures.gross)}</TableCell>
      <TableCell data-label="Monthly before adjustment" className="income-money">{money(figures.monthly)}</TableCell>
      <TableCell data-label="Adjustment">{savedAdjustmentLabel(income)}</TableCell>
      <TableCell data-label="Monthly adjusted" className="income-money"><strong>{money(figures.monthlyAdjusted)}</strong></TableCell>
    </TableRow>
    <TableRow hidden={!expanded} data-income-expansion><TableCell colSpan={6} id={id}>
      <div className="income-breakdown"><h5>Reported amounts · {source}</h5><p>Original reported dates and amounts.</p>
        {income.incomeAmountCollection.length === 0 ? <p>No amounts were returned for this source.</p> : <table aria-label={`Reported amounts for ${name}: ${source}`}><thead><tr><th>Reported date</th><th>Amount</th></tr></thead><tbody>
          {income.incomeAmountCollection.map((amount, index) => <tr key={`${amount.id}-${index}`}><td>{incomeRecordedDate(amount.date)}</td><td>{money(amount.amount)}</td></tr>)}
        </tbody></table>}
      </div>
    </TableCell></TableRow>
  </>
}

export function HouseholdIncomeSection({
  benefit,
  canEdit,
  onUpdated,
  onHistory,
  onProgram,
}: {
  benefit: BenefitDetailDto
  canEdit: boolean
  onUpdated: (updated: BenefitDetailDto) => void
  onHistory?: () => void
  onProgram?: () => void
}) {
  const assessmentState = useIncomeAssessment(benefit)
  const { assessment, loading, error } = assessmentState
  const [bulkOpen, setBulkOpen, bulkToken] = useBenefitEdit(benefit.concurrencyToken, "household-income")
  const bulkBusy = useRef(false)
  const bulkOrigin = useRef<HTMLButtonElement | null>(null)
  const heading = useRef<HTMLHeadingElement | null>(null)
  const isPseIntake = benefit.benefitApplicationType === 0 ||
    benefit.pseBenefitDetails != null ||
    benefit.requestedBenefits.some((program) => program.programId === 0)
  const initialIncomeType = isPseIntake ? ONE_MONTH : THREE_MONTH
  const [defaultIncomeType, setDefaultIncomeType] = useIncomeHistoryPreference(initialIncomeType)
  const [bulkType, setBulkType] = useBenefitIncomeDraft("household-income", "incomeType", String(initialIncomeType))
  const [bulkSaving, setBulkSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const [bulkError, setBulkError] = useBenefitIncomeDraft("household-income", "error", null)
  const sorted = incomeMembersByDob(benefit.personCollection)
  const [bulkMembers, setBulkMembers] = useBenefitIncomeDraft("household-income", "members", () => sorted.map(incomeMemberBaseline))
  const [bulkAnchorDate, setBulkAnchorDate] = useBenefitIncomeDraft("household-income", "anchorDate", () => captureIncomeAnchor(benefit.applicationDate))
  const selectedIncomeType = householdIncomeTypeLabel(benefit.personCollection)
  const displayedIncomeType = selectedIncomeType === "Not selected"
    ? `${incomeHistoryLabels[defaultIncomeType] ?? "the selected type"} (new entries)`
    : selectedIncomeType
  const monthlyFigures = benefit.personCollection.flatMap(person => person.incomeCollection.map(income => savedIncomeFigures(income).monthly))
  const monthlyBeforeAdjustment = monthlyFigures.length > 0 && monthlyFigures.every(value => value !== null)
    ? monthlyFigures.reduce<number>((total, value) => total + (value ?? 0), 0) : null

  const applyHouseholdIncomeType = async () => {
    if (bulkBusy.current || bulkSaving || reviewing || !canEdit) return
    setBulkError(null)
    const incomeType = Number(bulkType)
    const planned = bulkMembers.map((person) => ({ person, result: bulkIncomeLines(person, incomeType, bulkAnchorDate) }))
    const failure = planned.find((entry) => entry.result.error)
    if (failure?.result.error) {
      setBulkError(failure.result.error)
      return
    }
    if (planned.every((entry) => entry.person.incomeCollection.length === 0)) {
      if (!setDefaultIncomeType(incomeType)) return
      setBulkOpen(false)
      toast.success(`New income entries will start as ${incomeHistoryLabels[incomeType] ?? "the selected type"}.`)
      return
    }

    bulkBusy.current = true
    setBulkSaving(true)
    try {
      const updated = await api.put<BenefitDetailDto>(
        `/api/benefitapplications/${benefit.id}/household-income`,
        {
          concurrencyToken: bulkToken,
          // Include empty collections too: the server requires a complete household snapshot,
          // so a member removed between planning and save cannot leave stale income behind.
          members: planned.map((entry) => ({ personId: entry.person.id, income: entry.result.lines })),
        },
      )
      onUpdated(updated)
      setDefaultIncomeType(incomeType)
      setBulkOpen(false)
      toast.success(`Income Type set to ${incomeHistoryLabels[incomeType] ?? "the selected type"} for the household.`)
    } catch (error) {
      setBulkError(error instanceof Error ? error.message : "The household income type could not be updated.")
    } finally {
      bulkBusy.current = false
      setBulkSaving(false)
    }
  }

  return (
    <TooltipProvider><section className="income-workspace" aria-labelledby="income-heading">
      <header><h2 ref={heading} tabIndex={-1} id="income-heading">Income</h2>
        <div className="income-summary"><span>Saved monthly adjusted <strong>{money(benefit.monthlyAdjustedIncome)}</strong></span><span>Evidence <strong>{loading ? "Loading…" : incomeEvidenceLabel(assessment)}</strong></span><span>Reported periods <strong>{householdIncomePeriods(benefit.personCollection)}</strong></span></div>
      </header>
      {(error || assessment?.message) && <div className="income-top-notice" role="status"><span>{error || assessment?.message}</span><Button size="sm" variant="ghost" onClick={() => { const target = document.getElementById("income-screening-heading"); target?.scrollIntoView({ block: "start" }); target?.focus() }}>View screening</Button></div>}
      <Card className="income-ledger">
        <CardHeader><div className="income-toolbar"><div><CardTitle><h3>Income by household member</h3></CardTitle><CardDescription>Income Type: {displayedIncomeType}</CardDescription></div>
          <div className="income-toolbar-actions">{onHistory && <Button variant="ghost" size="sm" aria-label="View application history" onClick={onHistory}><IconHistory className="size-4" aria-hidden="true" />Application history</Button>}
            {canEdit && <Button variant="outline" size="sm" onClick={event => {
              if (!setBulkOpen(true)) return
              bulkOrigin.current = event.currentTarget
              const savedTypes = benefit.personCollection.flatMap(person => person.incomeCollection.map(income => income.incomeType))
              const savedType = savedTypes.length > 0 && savedTypes.every(incomeType => incomeType === savedTypes[0]) ? savedTypes[0] : defaultIncomeType
              setBulkType(String(savedType))
              setBulkMembers(sorted.map(incomeMemberBaseline))
              setBulkAnchorDate(captureIncomeAnchor(benefit.applicationDate))
              setBulkError(null)
            }}>Income Type</Button>}
          </div>
        </div></CardHeader>
        <CardContent>
          {benefit.personCollection.length === 0 ? <p className="income-note">Add household members before reporting income.</p> : sorted.map((person, index) => <MemberIncomeRecord key={`${person.id}-${index}`} benefit={benefit} person={person} canEdit={canEdit} defaultIncomeType={defaultIncomeType} onUpdated={onUpdated} assessment={assessment} />)}
          <div className="income-ledger-total"><span>From sources, monthly before adjustment <strong>{money(monthlyBeforeAdjustment)}</strong></span><span>Saved household monthly adjusted <strong>{money(benefit.monthlyAdjustedIncome)}</strong></span></div>
        </CardContent>
      </Card>
      <p className="income-note income-ledger-note">Household adjusted income is the saved server total. Source figures are derived from recorded amounts and adjustments.</p>
      <IncomeAssessmentBanner benefit={benefit} canEdit={canEdit} onUpdated={onUpdated} state={assessmentState} onProgram={onProgram} />
      <Dialog open={bulkOpen && !reviewing} onOpenChange={(open) => !bulkBusy.current && !bulkSaving && setBulkOpen(open)}>
        <DialogContent className="income-form-dialog" onCloseAutoFocus={event => { event.preventDefault(); if (!reviewing) (bulkOrigin.current?.isConnected ? bulkOrigin.current : heading.current)?.focus() }}>
          <DialogHeader>
            <DialogTitle>Set household Income Type</DialogTitle>
            <DialogDescription>
              {bulkMembers.some((person) => person.incomeCollection.length > 0)
                ? "This updates every existing income entry for every member in one transaction. Amounts, sources, and observed dates are preserved. If the selected period would discard a non-zero month, the update is refused before anything is saved."
                : "Choose the history type that new income rows should start with. You can still choose a different type on an individual row before saving."}
            </DialogDescription>
          </DialogHeader>
          <div className="income-panel-body space-y-1.5">
            {bulkError && <p role="alert" className="text-sm text-destructive">{bulkError}</p>}
            <Label htmlFor="household-income-type">Income Type</Label>
            <Select value={bulkType} disabled={bulkSaving} onValueChange={setBulkType}>
              <SelectTrigger id="household-income-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {historyTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={bulkSaving} onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button disabled={bulkSaving || !canEdit} onClick={applyHouseholdIncomeType}>
              {bulkSaving && <IconCurrencyDollar className="size-4 animate-pulse" />}
              Apply to household
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section></TooltipProvider>
  )
}
